import { useState, useRef, useCallback } from 'react';
import { apiClient } from '../services/api';

export type MicState = 'idle' | 'recording' | 'transcribing' | 'error';

export function useMicrophone(onTranscript: (text: string) => void, context?: string, language = 'en') {
  const [micState, setMicState] = useState<MicState>('idle');
  const [micError, setMicError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const startRecording = useCallback(async () => {
    setMicError(null);
    setMicState('idle');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setMicState('transcribing');
        try {
          const result = await apiClient.transcribeAudio(blob, language, context);
          onTranscript(result.transcript);
          setMicState('idle');
        } catch {
          setMicError('Transcription failed. Please try again.');
          setMicState('error');
        }
      };

      mediaRecorder.start();
      setMicState('recording');
    } catch {
      setMicError('Microphone access denied.');
      setMicState('error');
    }
  }, [onTranscript, context]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;
  }, []);

  const clearError = useCallback(() => {
    setMicError(null);
    setMicState('idle');
  }, []);

  return { micState, micError, startRecording, stopRecording, clearError };
}
